import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  Tabs,
  Tab,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  useTheme,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Translate as TranslateIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { delphiService, STATIC_BANNER_PLATES } from '../../services/delphiApi';
import type { GenerationSession, Iteration, SessionAsset, GeminiModel } from '../../services/delphiApi';
import type { Article } from '../../models';
import MarkdownDiffViewer from './MarkdownDiffViewer';

interface DelphiAiAssistantProps {
  open: boolean;
  onClose: () => void;
  article: Partial<Article>;
  currentMarkdown: string;
  onApplyContent: (newMarkdown: string, newTitle?: string, newDescription?: string, newSummary?: string) => void;
  onApplyHindi: (data: { title_hi?: string; description_hi?: string; summary_hi?: string; content_markdown_hi?: string }) => void;
  onApplyImage: (imageUrl: string, imageId?: number) => void;
}

const QUICK_INSTRUCTIONS = [
  "Add a detailed pitch and weather report section",
  "Add Fantasy XI captain & vice-captain tips with reasons",
  "Expand head-to-head records and key player battles",
  "Make the opening paragraph more engaging and punchy",
  "Add probable playing 11 for both teams",
  "Fix grammar, flow, and add structured markdown headers",
];

export const DelphiAiAssistant: React.FC<DelphiAiAssistantProps> = ({
  open,
  onClose,
  article,
  currentMarkdown,
  onApplyContent,
  onApplyHindi,
  onApplyImage,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [activeTab, setActiveTab] = useState<number>(0);
  const [loadingSession, setLoadingSession] = useState<boolean>(false);
  const [session, setSession] = useState<GenerationSession | null>(null);
  const [models, setModels] = useState<GeminiModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('gemini-2.5-pro');

  // Refine tab state
  const [refineInstruction, setRefineInstruction] = useState<string>('');
  const [refining, setRefining] = useState<boolean>(false);
  const [refinedIteration, setRefinedIteration] = useState<Iteration | null>(null);
  const [refineError, setRefineError] = useState<string | null>(null);

  // Translation tab state
  const [translating, setTranslating] = useState<boolean>(false);
  const [translatedAsset, setTranslatedAsset] = useState<SessionAsset | null>(null);
  const [translateError, setTranslateError] = useState<string | null>(null);

  // Image generation tab state
  const [imagePrompt, setImagePrompt] = useState<string>('');
  // 'static' draws the rules-based banner (flags, team colours, stadium picture) with no model call
  const [imageStyle, setImageStyle] = useState<'ai' | 'static'>('ai');
  const [imagePlate, setImagePlate] = useState('');
  const [aspectRatio, setAspectRatio] = useState<string>('16:9');
  const [resolution, setResolution] = useState<string>('2K');
  const [generatingImage, setGeneratingImage] = useState<boolean>(false);
  const [generatedImageAsset, setGeneratedImageAsset] = useState<{ url: string; id?: number } | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  // Global status/feedback
  const [statusMessage, setStatusMessage] = useState<{ text: string; severity: 'success' | 'info' | 'error' } | null>(null);

  // Load models once
  useEffect(() => {
    delphiService.listModels()
      .then((res) => {
        setModels(res || []);
        if (res && res.length > 0) {
          const proModel = res.find((m) => m.family === 'pro') || res[0];
          setSelectedModel(proModel.id);
        }
      })
      .catch((err) => console.error("Error loading Gemini models:", err));
  }, []);

  // Initialize or fetch session when opened
  useEffect(() => {
    if (!open || !article?.id) return;

    const resolveSession = async () => {
      try {
        setLoadingSession(true);
        setStatusMessage(null);
        let activeSession: GenerationSession | null = null;

        // 1. Check article.delphi_session_id
        if (article.delphi_session_id) {
          try {
            activeSession = await delphiService.getSession(article.delphi_session_id);
          } catch (e) {
            console.warn("Could not fetch session by delphi_session_id:", e);
          }
        }

        // 2. Query sessions by article_id
        if (!activeSession) {
          try {
            const listRes = await delphiService.listSessions({
              object_id: article.id,
              page_size: 5,
            } as any);
            const found = listRes.results?.find((s) => s.published_article === article.id) || listRes.results?.[0];
            if (found) {
              activeSession = await delphiService.getSession(found.id);
            }
          } catch (e) {
            console.warn("Could not find session by article_id:", e);
          }
        }

        // 3. Fallback: Initialize session for cms.article
        if (!activeSession) {
          try {
            activeSession = await delphiService.initSession({
              app_label: 'cms',
              model: 'article',
              object_id: article.id!,
              additional_instruction: "Article refinement from CMS Editor",
              llm_model: selectedModel,
            });
          } catch (e) {
            console.warn("Could not init new session for article:", e);
          }
        }

        if (activeSession) {
          setSession(activeSession);
          // Pre-populate visual prompt if empty
          if (!imagePrompt) {
            const headline = article.title_en || article.title || "Cricket Match Preview";
            setImagePrompt(`Cinematic banner for ${headline}, vibrant stadium atmosphere, dramatic lighting, high quality 3D render`);
          }
        }
      } catch (err: any) {
        console.error("Error in resolveSession:", err);
      } finally {
        setLoadingSession(false);
      }
    };

    resolveSession();
  }, [open, article?.id]);

  // Handle LLM Refine Content
  const handleRefine = async () => {
    if (!session || !refineInstruction.trim()) return;
    try {
      setRefining(true);
      setRefineError(null);
      setStatusMessage(null);

      // Save current editor draft markdown to session first so LLM works on latest edits
      if (currentMarkdown) {
        try {
          await delphiService.saveManualEdit(session.id, {
            title: article.title_en || article.title,
            description: article.description_en || article.description,
            summary: article.summary_en || article.summary,
            text_content: currentMarkdown,
          });
        } catch (e) {
          console.warn("Error saving manual edit before refine:", e);
        }
      }

      // Invoke LLM refinement
      const newIteration = await delphiService.refineContent(session.id, {
        instruction: refineInstruction.trim(),
        llm_model: selectedModel,
      });

      setRefinedIteration(newIteration);
      setStatusMessage({
        text: `Content refined successfully using ${selectedModel}! Review changes below.`,
        severity: 'success',
      });
    } catch (err: any) {
      console.error("Error refining content:", err);
      setRefineError(err.response?.data?.error || err.message || "Failed to refine content with LLM");
    } finally {
      setRefining(false);
    }
  };

  // Handle Apply Refined Content
  const handleApplyRefined = () => {
    if (!refinedIteration) return;
    onApplyContent(
      refinedIteration.text_content,
      refinedIteration.title,
      refinedIteration.description,
      refinedIteration.summary
    );
    setStatusMessage({
      text: "Refined content applied to current draft! You can now review diff or translate to Hindi.",
      severity: 'success',
    });
  };

  // Handle Translate to Hindi
  const handleTranslateToHindi = async () => {
    if (!session) return;
    try {
      setTranslating(true);
      setTranslateError(null);
      setStatusMessage(null);

      // Save current markdown to session latest iteration so translation translates the latest content
      const sourceText = refinedIteration?.text_content || currentMarkdown;
      try {
        await delphiService.saveManualEdit(session.id, {
          title: article.title_en || article.title,
          description: article.description_en || article.description,
          summary: article.summary_en || article.summary,
          text_content: sourceText,
        });
      } catch (e) {
        console.warn("Error saving edit before translation:", e);
      }

      const asset = await delphiService.translateToHindi(
        session.id,
        refinedIteration?.id,
        undefined,
        selectedModel
      );

      setTranslatedAsset(asset);
      setStatusMessage({
        text: "Hindi translation generated successfully! Click 'Apply Hindi to Draft' to update the Hindi fields.",
        severity: 'success',
      });
    } catch (err: any) {
      console.error("Error translating to Hindi:", err);
      setTranslateError(err.response?.data?.error || err.message || "Failed to translate content to Hindi");
    } finally {
      setTranslating(false);
    }
  };

  // Handle Apply Hindi
  const handleApplyHindiTranslation = () => {
    if (!translatedAsset) return;
    const content = translatedAsset.content || {};
    const titleHi = translatedAsset.title_hi || content.title || "";
    const descHi = translatedAsset.description_hi || content.description || "";
    const summaryHi = translatedAsset.summary_hi || content.summary || "";
    const markdownHi = content.content_markdown || content.text_content_hi || "";

    onApplyHindi({
      title_hi: titleHi,
      description_hi: descHi,
      summary_hi: summaryHi,
      content_markdown_hi: markdownHi,
    });

    setStatusMessage({
      text: "Hindi translation applied to article draft!",
      severity: 'success',
    });
  };

  // Handle Generate Image
  const handleGenerateImage = async () => {
    if (!session) return;
    try {
      setGeneratingImage(false);
      setGeneratingImage(true);
      setImageError(null);
      setStatusMessage(null);

      const res = await delphiService.generateAssets(session.id, {
        asset_types: ['VISUAL_IMAGE'],
        banner_options:
          imageStyle === 'static'
            ? { style: 'static', plate: imagePlate || undefined }
            : { aspect_ratio: aspectRatio, resolution: resolution },
        visual_prompt_override: imageStyle === 'ai' ? imagePrompt.trim() || undefined : undefined,
        llm_model: selectedModel,
        async: true,
      });

      // Handle async task polling or direct synchronous response
      if ((res as any).task_id) {
        const taskId = (res as any).task_id;
        setStatusMessage({ text: "Banner generation queued, polling progress...", severity: 'info' });

        // Poll task
        let attempts = 0;
        const maxAttempts = 30;
        const checkPoll = async () => {
          attempts++;
          try {
            const task = await delphiService.getTaskStatus(taskId);
            if (task.status === 'SUCCESS' || task.status === 'finished') {
              // Fetch latest assets
              const assets = await delphiService.listAssets(session.id);
              const imgAsset = assets.find((a) => a.asset_type === 'VISUAL_IMAGE');
              if (imgAsset && (imgAsset.content?.image_url || imgAsset.content?.url)) {
                setGeneratedImageAsset({
                  url: imgAsset.content.image_url || imgAsset.content.url,
                  id: imgAsset.content.image_id,
                });
                setStatusMessage({ text: "Feature image generated successfully!", severity: 'success' });
              }
              setGeneratingImage(false);
            } else if (task.status === 'FAILURE' || task.status === 'FAILED') {
              setImageError(task.result?.error || "Image generation failed in background task");
              setGeneratingImage(false);
            } else if (attempts < maxAttempts) {
              setTimeout(checkPoll, 2500);
            } else {
              setImageError("Image generation timed out. Please try again.");
              setGeneratingImage(false);
            }
          } catch (e: any) {
            if (attempts < maxAttempts) {
              setTimeout(checkPoll, 2500);
            } else {
              setGeneratingImage(false);
            }
          }
        };
        setTimeout(checkPoll, 2000);
      } else {
        // Synchronous response
        const assets = res as SessionAsset[];
        const imgAsset = assets.find((a) => a.asset_type === 'VISUAL_IMAGE');
        if (imgAsset && (imgAsset.content?.image_url || imgAsset.content?.url)) {
          setGeneratedImageAsset({
            url: imgAsset.content.image_url || imgAsset.content.url,
            id: imgAsset.content.image_id,
          });
          setStatusMessage({ text: "Feature image generated successfully!", severity: 'success' });
        }
        setGeneratingImage(false);
      }
    } catch (err: any) {
      console.error("Error generating image:", err);
      setImageError(err.response?.data?.error || err.message || "Failed to generate image");
      setGeneratingImage(false);
    }
  };

  // Handle Apply Image
  const handleApplyImageToArticle = () => {
    if (!generatedImageAsset?.url) return;
    onApplyImage(generatedImageAsset.url, generatedImageAsset.id);
    setStatusMessage({
      text: "Featured image applied to article draft!",
      severity: 'success',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '80vh',
          maxHeight: '92vh',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 2.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
              color: '#fff',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Delphi AI Assistant
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Refine content with LLM, translate to Hindi, & generate AI match banners
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Model selector */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="llm-model-select-label">Model</InputLabel>
            <Select
              labelId="llm-model-select-label"
              value={selectedModel}
              label="Model"
              onChange={(e) => setSelectedModel(e.target.value)}
              sx={{ height: 36, fontSize: '0.825rem' }}
            >
              {models.length > 0 ? (
                models.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label} ({m.family})
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="gemini-2.5-pro">Gemini 2.5 Pro</MenuItem>
              )}
            </Select>
          </FormControl>

          {session && (
            <Chip
              label={`Session #${session.id}`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
          )}

          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2.5, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' } }}
        >
          <Tab
            icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="1. Refine / Edit Content"
          />
          <Tab
            icon={<TranslateIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="2. Re-translate to Hindi"
          />
          <Tab
            icon={<ImageIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="3. Re-generate Feature Image"
          />
        </Tabs>
      </Box>

      {/* Feedback Banner */}
      {statusMessage && (
        <Alert severity={statusMessage.severity} onClose={() => setStatusMessage(null)} sx={{ m: 2, mb: 0 }}>
          {statusMessage.text}
        </Alert>
      )}

      {loadingSession && <LinearProgress sx={{ height: 3 }} />}

      {/* Content Body */}
      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* TAB 0: REFINE CONTENT */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Card variant="outlined" sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LightbulbIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                Refine Draft with Custom AI Instructions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Instruct Delphi to update the live article draft, add missing tactical sections, pitch conditions, or fantasy tips.
              </Typography>

              {/* Quick Prompt Chips */}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.8 }}>
                Quick Instruction Suggestions:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {QUICK_INSTRUCTIONS.map((chipText) => (
                  <Chip
                    key={chipText}
                    label={chipText}
                    size="small"
                    clickable
                    onClick={() => {
                      setRefineInstruction((prev) =>
                        prev ? `${prev}\n- ${chipText}` : chipText
                      );
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
                    }}
                  />
                ))}
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Type your instruction to the LLM (e.g. 'Add a section analyzing the dew factor at Greenfield Stadium and suggest 2 differential captain picks for fantasy managers')..."
                value={refineInstruction}
                onChange={(e) => setRefineInstruction(e.target.value)}
                disabled={refining}
                variant="outlined"
              />

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Current Draft: {currentMarkdown ? `${currentMarkdown.trim().split(/\s+/).filter(Boolean).length} words` : '0 words'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={refining ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  disabled={refining || !refineInstruction.trim() || !session}
                  onClick={handleRefine}
                  sx={{
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
                  }}
                >
                  {refining ? "Refining Content..." : "Refine Content"}
                </Button>
              </Stack>
            </Card>

            {refineError && <Alert severity="error">{refineError}</Alert>}

            {/* Refined Result & Diff Preview */}
            {refinedIteration && (
              <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: isDark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Refined Content (Iteration v{refinedIteration.version})
                    </Typography>
                    {refinedIteration.llm_metadata?.tokens?.total_tokens && (
                      <Chip
                        label={`${refinedIteration.llm_metadata.tokens.total_tokens} tokens`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Stack>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleApplyRefined}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Apply Refinement to Draft
                  </Button>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                    Changes Diff (Current Draft vs Refined Output):
                  </Typography>
                  <MarkdownDiffViewer
                    original={currentMarkdown}
                    modified={refinedIteration.text_content}
                    originalTitle="Current Draft"
                    modifiedTitle={`Refined Version (v${refinedIteration.version})`}
                    height={400}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* TAB 1: RE-TRANSLATE TO HINDI */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Card variant="outlined" sx={{ p: 2.5, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TranslateIcon sx={{ color: 'primary.main' }} />
                Re-translate Final English Version to Hindi
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Once you are comfortable with your English draft or after applying LLM refinements, you can re-translate the entire article (Title, Description, and Markdown Content) to Hindi with one click.
              </Typography>

              <Box sx={{ p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  English Source to Translate:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {article.title_en || article.title || "No Title"}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {currentMarkdown ? `${currentMarkdown.trim().split(/\s+/).filter(Boolean).length} words ready to translate` : 'No English content'}
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={translating ? <CircularProgress size={18} color="inherit" /> : <TranslateIcon />}
                disabled={translating || !session || !currentMarkdown}
                onClick={handleTranslateToHindi}
                sx={{
                  px: 4,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                }}
              >
                {translating ? "Translating to Hindi with AI..." : "Translate to Hindi"}
              </Button>
            </Card>

            {translateError && <Alert severity="error">{translateError}</Alert>}

            {/* Translated Hindi Preview */}
            {translatedAsset && (
              <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.06)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Generated Hindi Translation
                    </Typography>
                  </Stack>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleApplyHindiTranslation}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Apply Hindi to Draft
                  </Button>
                </Box>

                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Hindi Title:
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {translatedAsset.title_hi || translatedAsset.content?.title || "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Hindi Description:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translatedAsset.description_hi || translatedAsset.content?.description || "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Hindi Summary:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translatedAsset.summary_hi || translatedAsset.content?.summary || "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                      Hindi Content Markdown:
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={12}
                      value={translatedAsset.content?.content_markdown || ""}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontFamily: 'Consolas, Monaco, monospace',
                          fontSize: '0.85rem',
                          lineHeight: 1.6,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* TAB 2: RE-GENERATE FEATURE IMAGE */}
        {activeTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Card variant="outlined" sx={{ p: 2.5, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon sx={{ color: 'secondary.main' }} />
                Generate / Re-generate Article Feature Image
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create a high-impact AI feature banner tailored to this article. Customize the prompt, aspect ratio, and resolution.
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel id="image-style-label">Banner style</InputLabel>
                  <Select
                    labelId="image-style-label"
                    value={imageStyle}
                    label="Banner style"
                    onChange={(e) => setImageStyle(e.target.value as 'ai' | 'static')}
                  >
                    <MenuItem value="ai">AI image (Gemini)</MenuItem>
                    <MenuItem value="static">Static (flags, team colours, stadium)</MenuItem>
                  </Select>
                </FormControl>
                {imageStyle === 'static' && (
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="image-plate-label">Picture</InputLabel>
                    <Select labelId="image-plate-label" value={imagePlate} label="Picture" onChange={(e) => setImagePlate(e.target.value)}>
                      {STATIC_BANNER_PLATES.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>

              {imageStyle === 'ai' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Image Prompt / Visual Style"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="e.g. Cinematic wide banner of India vs West Indies cricket clash at Greenfield stadium, energetic lighting, vibrant green grass, realistic 3D render, high definition..."
                sx={{ mb: 2 }}
              />
              )}

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {imageStyle === 'ai' && (
                  <>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="aspect-ratio-label">Aspect Ratio</InputLabel>
                  <Select
                    labelId="aspect-ratio-label"
                    value={aspectRatio}
                    label="Aspect Ratio"
                    onChange={(e) => setAspectRatio(e.target.value)}
                  >
                    <MenuItem value="16:9">16:9 (Article Banner)</MenuItem>
                    <MenuItem value="1:1">1:1 (Square)</MenuItem>
                    <MenuItem value="4:3">4:3 (Classic)</MenuItem>
                    <MenuItem value="9:16">9:16 (Shorts/Story)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="resolution-label">Resolution</InputLabel>
                  <Select
                    labelId="resolution-label"
                    value={resolution}
                    label="Resolution"
                    onChange={(e) => setResolution(e.target.value)}
                  >
                    <MenuItem value="2K">2K (High Res)</MenuItem>
                    <MenuItem value="1080p">1080p (Standard)</MenuItem>
                    <MenuItem value="4K">4K (Ultra)</MenuItem>
                  </Select>
                </FormControl>
                  </>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={generatingImage ? <CircularProgress size={18} color="inherit" /> : <ImageIcon />}
                  disabled={generatingImage || !session}
                  onClick={handleGenerateImage}
                  sx={{
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)',
                  }}
                >
                  {generatingImage ? "Generating Image..." : "Generate Feature Image"}
                </Button>
              </Stack>
            </Card>

            {imageError && <Alert severity="error">{imageError}</Alert>}

            {/* Generated Image Preview Card */}
            {generatedImageAsset?.url && (
              <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: isDark ? 'rgba(236,72,153,0.1)' : 'rgba(236,72,153,0.06)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Generated Feature Image
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleApplyImageToArticle}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Set as Featured Image
                  </Button>
                </Box>

                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', bgcolor: isDark ? '#0a0a0a' : '#f5f5f5' }}>
                  <img
                    src={generatedImageAsset.url}
                    alt="Generated Feature Image"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      borderRadius: 8,
                      objectFit: 'contain',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3, borderTop: `1px solid ${theme.palette.divider}`, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          Delphi Content Intelligence • Connected to Gemini
        </Typography>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelphiAiAssistant;
